import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';
import Text from '../components/shared/Text';

type ViewMode = 'list' | 'grid';

function StockBadge({ qty }: { qty: number }) {
    if (qty <= 0) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-600 border border-rose-100">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span> Out of Stock
        </span>
    );
    if (qty < 5) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-100">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Low ({qty})
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> {qty} in stock
        </span>
    );
}

function ProductsIndex() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total_pages: 1,
        total_count: 0,
        current_page: 1,
        per_page: 25
    });
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        return (localStorage.getItem('productsViewMode') as ViewMode) || 'list';
    });

    useEffect(() => {
        loadProducts();
    }, [search, currentPage]);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const response = await api.getProducts({ q: search, page: currentPage });
            
            // Handle both {success, data, meta} and direct data responses
            const productsData = response.data?.products || response.products || (Array.isArray(response.data) ? response.data : []);
            setProducts(productsData);

            const meta = response.meta || response.data?.meta;
            if (meta) {
                setPagination({
                    total_pages: meta.total_pages || 1,
                    total_count: meta.total_count || 0,
                    current_page: meta.current_page || 1,
                    per_page: meta.per_page || 25
                });
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this product?')) return;
        try {
            await api.deleteProduct(id);
            loadProducts();
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleViewChange = (mode: ViewMode) => {
        setViewMode(mode);
        localStorage.setItem('productsViewMode', mode);
    };

    if (loading) return <TableSkeleton />;
    if (error) return <div className="p-8 text-rose-600 font-medium">Error: {error}</div>;

    return (
        <div className="mt-6 px-1">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <Text variant="h2" color="primary">Products</Text>
                    <Text variant="small" color="slate" className="mt-0.5">
                        {pagination.total_count} items in your inventory
                    </Text>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to="/products/new"
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors text-sm shadow-sm shadow-indigo-200"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        New Product
                    </Link>
                    <Link
                        to="/products/manage"
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors text-sm"
                    >
                        Quick Entry
                    </Link>
                </div>
            </div>

            {/* Search + View Toggle */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex-1 relative">
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search products by name, SKU..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                    />
                </div>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
                    <button
                        onClick={() => handleViewChange('list')}
                        title="List view"
                        className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                    </button>
                    <button
                        onClick={() => handleViewChange('grid')}
                        title="Grid view"
                        className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* ── LIST VIEW ── */}
            <div className={`transition-all duration-300 ${viewMode === 'list' ? 'opacity-100' : 'hidden'}`}>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left py-3 px-4"><Text variant="label" color="slate">Product</Text></th>
                                    <th className="text-left py-3 px-4 hidden sm:table-cell"><Text variant="label" color="slate">SKU</Text></th>
                                    <th className="text-center py-3 px-4 hidden md:table-cell"><Text variant="label" color="slate">Type</Text></th>
                                    <th className="text-right py-3 px-4"><Text variant="label" color="slate">MRP</Text></th>
                                    <th className="text-center py-3 px-4 hidden sm:table-cell"><Text variant="label" color="slate">QR</Text></th>
                                    <th className="text-right py-3 px-4"><Text variant="label" color="slate">Stock</Text></th>
                                    <th className="text-right py-3 px-4"><Text variant="label" color="slate">Actions</Text></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {products.map((product: any) => (
                                    <tr key={product.id} className="group hover:bg-indigo-50/30 transition-colors duration-150">
                                        <td className="py-3.5 px-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center text-base flex-shrink-0 group-hover:bg-indigo-100 transition-colors overflow-hidden">
                                                    {product.image_url ? (
                                                        <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        '📦'
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <Text weight="semibold" color="primary" className="group-hover:text-indigo-700 transition-colors">
                                                            {product.name}
                                                        </Text>
                                                        {product.requires_installation && (
                                                            <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[9px] font-black uppercase border border-emerald-100 shadow-sm whitespace-nowrap group-hover:animate-pulse">🔧 Install</span>
                                                        )}
                                                        {product.discontinued && (
                                                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-600 rounded text-[9px] font-black uppercase">Discontinued</span>
                                                        )}
                                                    </div>
                                                    {product.category_name && (
                                                        <Text variant="small" color="slate" className="mt-0.5">{product.category_name}</Text>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3.5 px-4 hidden sm:table-cell">
                                            <Text variant="mono" color="slate" className="text-xs">{product.sku || '—'}</Text>
                                        </td>
                                        <td className="py-3.5 px-4 text-center hidden md:table-cell">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${product.serialized ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                                                {product.serialized ? '💎 Serialized' : '📦 Standard'}
                                            </span>
                                        </td>
                                        <td className="py-3.5 px-4 text-right">
                                            <Text weight="bold" color="primary">₹{product.mrp?.toLocaleString()}</Text>
                                        </td>
                                        <td className="py-3.5 px-4 text-center hidden sm:table-cell">
                                            {product.qr_code_url
                                                ? <img src={product.qr_code_url} alt="QR" className="w-8 h-8 object-contain mx-auto rounded" />
                                                : <Text variant="small" color="slate" className="opacity-30">—</Text>
                                            }
                                        </td>
                                        <td className="py-3.5 px-4 text-right">
                                            <StockBadge qty={product.stock_quantity} />
                                        </td>
                                        <td className="py-3.5 px-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <Link to={`/products/${product.id}`} className="px-2.5 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">View</Link>
                                                <Link to={`/products/${product.id}/edit`} className="px-2.5 py-1.5 text-xs font-semibold text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">Edit</Link>
                                                <button onClick={() => handleDelete(product.id)} className="px-2.5 py-1.5 text-xs font-semibold text-rose-500 hover:bg-rose-50 rounded-lg transition-colors">Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {products.length === 0 && (
                        <div className="py-16 text-center">
                            <div className="text-4xl mb-3">📦</div>
                            <Text weight="semibold" color="primary">No products found</Text>
                            <Text variant="small" color="slate" className="mt-1">Try adjusting your search or add a new product.</Text>
                        </div>
                    )}
                </div>
            </div>

            {/* ── GRID VIEW ── */}
            <div className={`transition-all duration-300 ${viewMode === 'grid' ? 'opacity-100' : 'hidden'}`}>
                {products.length === 0 ? (
                    <div className="py-16 text-center bg-white rounded-2xl border border-slate-200">
                        <div className="text-4xl mb-3">📦</div>
                        <Text weight="semibold" color="primary">No products found</Text>
                        <Text variant="small" color="slate" className="mt-1">Try adjusting your search or add a new product.</Text>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {products.map((product: any) => (
                            <div
                                key={product.id}
                                className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-200 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col"
                            >
                                {/* Card top: QR or icon */}
                                <div className="relative h-28 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center border-b border-slate-100">
                                    {product.image_url ? (
                                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                    ) : product.qr_code_url ? (
                                        <img src={product.qr_code_url} alt="QR" className="h-20 w-20 object-contain" />
                                    ) : (
                                        <span className="text-5xl opacity-30">📦</span>
                                    )}
                                    {product.discontinued && (
                                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-rose-500 text-white rounded-full text-[9px] font-black uppercase">Discontinued</span>
                                    )}
                                    {product.serialized && (
                                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-indigo-500 text-white rounded-full text-[9px] font-black uppercase">Serialized</span>
                                    )}
                                </div>

                                {/* Card body */}
                                <div className="p-4 flex-1 flex flex-col">
                                    <div className="mb-1">
                                        <div className="flex items-center gap-2 truncate">
                                            <Text variant="h3" color="primary" className="group-hover:text-indigo-700 transition-colors truncate" title={product.name}>
                                                {product.name}
                                            </Text>
                                            {product.requires_installation && (
                                                <span className="flex-shrink-0 px-1 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[8px] font-black uppercase border border-emerald-100 shadow-sm group-hover:animate-pulse">🔧</span>
                                            )}
                                        </div>
                                        {product.category_name && (
                                            <Text variant="small" color="slate" className="mt-0.5 truncate">{product.category_name}</Text>
                                        )}
                                        {product.sku && (
                                            <Text variant="mono" color="slate" className="text-[10px] mt-0.5">{product.sku}</Text>
                                        )}
                                    </div>

                                    <div className="mt-auto pt-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Text variant="label" color="slate">MRP</Text>
                                            <Text variant="h3" color="primary">₹{product.mrp?.toLocaleString()}</Text>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Text variant="label" color="slate">Stock</Text>
                                            <StockBadge qty={product.stock_quantity} />
                                        </div>
                                    </div>
                                </div>

                                {/* Card actions */}
                                <div className="px-4 pb-4 flex items-center gap-2 border-t border-slate-100 pt-3">
                                    <Link
                                        to={`/products/${product.id}`}
                                        className="flex-1 text-center py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    >
                                        View
                                    </Link>
                                    <Link
                                        to={`/products/${product.id}/edit`}
                                        className="flex-1 text-center py-2 text-xs font-semibold text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    >
                                        Edit
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(product.id)}
                                        className="flex-1 text-center py-2 text-xs font-semibold text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-6 border-t border-slate-100 pt-6">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

export default ProductsIndex;
